import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PAYOR_CLASSES,
  PAYOR_STATES,
  blankRcmPayor,
  contractState,
  filterRcmPayors,
  payorClassLabel,
  validateRcmPayor,
  type RcmPayor,
  type RcmPayorDraft,
  type RcmPayorList,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { useSession, canManage } from '../auth/session.js';
import { useLiveRefresh } from '../lib/useLiveRefresh.js';
import { Dd, MultiDd } from './Dropdown.js';
import { DateField } from './DateTimeField.js';
import { Modal } from './Modal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { PayorUsers, usersEditable } from './PayorUsers.js';
import { SaveButton, SaveCardAnchor, runSave, type SaveNotice } from './SaveToast.js';
import { payorErrText, payorSavedText, payorToggledText } from '../lib/saveCards.js';

/**
 * calendar#37 - Data & settings > Payors (Chase 10-08: the source of payors, between Locations and
 * Holidays). The practice's one payor list is RCM's Chargemaster > Payors (decision 24a, Pranav
 * 10-09): this reads it through GET /payors/all and saves through RCM (POST / PUT /payors,
 * forwarded with the user's token). The Calendar copies the same list on every load, and client
 * Demographics' Insurance / Payor dropdown and claims use it. Changes save right away (outside the
 * modal's Save). No delete here: a payor is turned off instead (deleting stays in Revenue Cycle).
 *
 * Chase 10-12: laid out as RCM's Payors tab (Search / State / Class, rows grouped under their
 * state, View · Edit · on/off), and it re-reads the list while open, so a payor added or changed in
 * Revenue Cycle shows here without a refresh.
 *
 * ehr#610 (Chase 10-22): View / Edit / Add open the payor in a pop-up (the house Modal) over the
 * list, so the list keeps its scroll position. Closing with unsaved edits (X, Cancel, Esc or a click
 * outside) asks Save / Don't save / Go back, as Note settings does (ehr#517).
 * ehr#611: the condensed Payor details layout, and under it "Users accepting this insurance"
 * (PayorUsers: the providers enrolled with this payor, the Provider Profile's same rows).
 *
 * ehr#681 follow-up: the pop-up's Save reads "Saving…" for at least 2 s; then the pop-up closes and
 * the drawer's save card ("Payor saved" / "Payor added") hangs under + Add payor, where the old
 * flash text was. A failure keeps the pop-up open with a red "Not saved" card above Save. Turning a
 * payor on or off shows its card there too.
 */

const RCM_BASE = 'https://rcm.credifyfast.com';
/** How often the open list re-reads RCM's payors (and at once when the tab comes back). */
const LIVE_MS = 8_000;
type Errs = ReturnType<typeof validateRcmPayor>;

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
const toDraft = (p: RcmPayor): RcmPayorDraft => ({ ...p });
const body = (d: RcmPayorDraft) => ({
  name: d.name.trim(),
  class: d.class,
  payerId: d.payerId.trim(),
  street: d.street.trim(),
  city: d.city.trim(),
  addrState: d.addrState,
  zip: d.zip.trim(),
  contractStart: d.contractStart,
  contractEnd: d.contractEnd,
  active: d.active,
});
const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : '';
};
const todayIso = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
/** RCM groups a payor under its address state; one with none sits under "—", after the states. */
const NO_STATE = '—';
const groupOf = (p: RcmPayor) => p.addrState || NO_STATE;

export function PayorsSettings() {
  const { user } = useSession();
  const canEdit = canManage(user?.role);
  const [list, setList] = useState<RcmPayorList | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [query, setQuery] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [draft, setDraft] = useState<RcmPayorDraft | null>(null);
  /** The panel shows a payor read-only (View) until Edit. */
  const [viewOnly, setViewOnly] = useState(false);
  const [errs, setErrs] = useState<Errs>({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  /** The card under + Add payor (a save from the pop-up, or a payor turned on / off). */
  const [listNote, setListNote] = useState<SaveNotice | null>(null);
  /** The pop-up's "Not saved" card, above its Save. */
  const [saveNote, setSaveNote] = useState<SaveNotice | null>(null);
  /** The draft as the pop-up opened (or switched to Edit), to tell unsaved edits. */
  const [opened, setOpened] = useState('');
  /** ehr#610: the Save / Don't save / Go back prompt is up. */
  const [ask, setAsk] = useState(false);
  // a live re-read never lands over a save or a toggle in flight (it would show the old row)
  const busy = useRef(false);
  busy.current = saving || !!toggling;

  const load = useCallback(() => {
    setLoadErr('');
    api
      .get<RcmPayorList>('/payors/all')
      .then(setList)
      .catch((e: unknown) => setLoadErr(msgOf(e)));
  }, []);
  useEffect(load, [load]);
  useLiveRefresh(() => {
    if (busy.current) return;
    api
      .get<RcmPayorList>('/payors/all')
      .then((r) => {
        if (!busy.current) setList(r);
      })
      .catch(() => {
        /* a missed re-read keeps what's shown; the next one catches up */
      });
  }, LIVE_MS);

  const payors = list?.payors ?? [];
  const groups = useMemo(() => {
    const st = new Set(states);
    const cl = new Set(classes);
    const rows = filterRcmPayors(payors, query, 'all', 'all').filter(
      (p) => (!st.size || st.has(groupOf(p))) && (!cl.size || cl.has(p.class)),
    );
    const by = new Map<string, RcmPayor[]>();
    for (const p of rows) by.set(groupOf(p), [...(by.get(groupOf(p)) ?? []), p]);
    return [...by.entries()].sort(([a], [b]) =>
      a === NO_STATE ? 1 : b === NO_STATE ? -1 : a.localeCompare(b),
    );
  }, [payors, query, states, classes]);
  const stateOptions = useMemo(() => {
    const used = new Set(payors.map(groupOf));
    return [...used]
      .sort((a, b) => (a === NO_STATE ? 1 : b === NO_STATE ? -1 : a.localeCompare(b)))
      .map((s) => ({ value: s, label: s }));
  }, [payors]);
  const today = todayIso();
  const activeCount = payors.filter((p) => p.active).length;

  const open = (d: RcmPayorDraft, readOnly = false) => {
    setDraft(d);
    setOpened(JSON.stringify(d));
    setViewOnly(readOnly);
    setErrs({});
    setSaveErr('');
    setSaveNote(null);
    setAsk(false);
  };
  const dirty = !!draft && !viewOnly && JSON.stringify(draft) !== opened;
  const close = () => {
    setDraft(null);
    setAsk(false);
  };
  /** X, Cancel, Esc, a click outside: unsaved edits ask first. */
  const requestClose = () => {
    if (saving) return;
    if (dirty) setAsk(true);
    else close();
  };
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  // Esc: caught on window before the Modal's and Data & settings' own Escape (document), so it
  // closes only the pop-up, and asks first when there are unsaved edits. An open dropdown or
  // calendar inside it just closes. The Credify confirm handles its own Esc.
  const popupOpen = !!draft;
  useEffect(() => {
    if (!popupOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('.cred-confirm-overlay')) return;
      e.stopPropagation();
      e.preventDefault();
      if (document.querySelector('.payor-modal .dd-open, .payor-modal .dtf-open')) {
        document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        return;
      }
      // ehr#611: the enrollment pop-up over a user row closes on its own (its Cancel)
      const enroll = document.querySelector<HTMLButtonElement>('.py-enroll-modal .modal-close');
      if (enroll) {
        enroll.click();
        return;
      }
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [popupOpen]);
  const set = <K extends keyof RcmPayorDraft>(k: K, v: RcmPayorDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const save = () => {
    if (!draft || saving || viewOnly) return;
    const e = validateRcmPayor(draft, payors);
    setErrs(e);
    if (Object.keys(e).length) {
      setAsk(false); // back to the form, where the problem shows
      return;
    }
    setSaving(true);
    setSaveErr('');
    setSaveNote(null);
    setListNote(null);
    const req = draft.id
      ? api.put<RcmPayorList>(`/payors/${draft.id}`, body(draft))
      : api.post<RcmPayorList & { id: string }>('/payors', body(draft));
    // the list takes the server's answer at once; the pop-up closes after the 2 s "Saving…"
    void runSave(
      req.then((r) => {
        setList({ available: r.available, payors: r.payors });
        return r;
      }),
      { ok: payorSavedText(!draft.id), err: payorErrText, okBelow: true },
    ).then((r) => {
      setSaving(false);
      if (r.ok) {
        setListNote(r.notice);
        close();
      } else {
        setSaveErr(msgOf(r.error));
        setSaveNote(r.notice);
        setAsk(false);
      }
    });
  };

  const toggle = (p: RcmPayor) => {
    if (!canEdit || toggling) return;
    setToggling(p.id);
    setListNote(null);
    // a switch has no "Saving…" text, so no 2 s hold: the card follows the server's answer
    void runSave(
      api.put<RcmPayorList>(`/payors/${p.id}`, body({ ...toDraft(p), active: !p.active })),
      {
        ok: payorToggledText(p.name, !p.active),
        err: (err) => `Could not change ${p.name}: ${payorErrText(err)}`,
        okBelow: true,
      },
      0,
    ).then((r) => {
      if (r.ok) setList(r.value);
      setListNote(r.ok ? r.notice : { ...r.notice, below: true });
      setToggling(null);
    });
  };

  if (loadErr && !list)
    return (
      <div className="note warn">
        Could not load the payor list: {loadErr}{' '}
        <button className="btn sm" onClick={load}>
          Try again
        </button>
      </div>
    );
  if (!list) return <div className="empty">Loading payors…</div>;
  if (!list.available)
    return (
      <div className="note warn">
        Revenue Cycle&apos;s payor list can&apos;t be read here yet. Until it can, payors are edited
        in{' '}
        <a href={RCM_BASE} target="_blank" rel="noreferrer">
          Revenue Cycle
        </a>{' '}
        (Chargemaster → Payors).
      </div>
    );

  const d = draft;
  const ro = viewOnly;
  return (
    <div className="payors">
      <div className="payors-bar save-host">
        <div className="payors-f payors-f-search">
          <label htmlFor="pySearch">Search payors</label>
          <input
            id="pySearch"
            type="search"
            className="payors-search"
            placeholder="Name, state, class, or payer ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="payors-f payors-dd">
          <label>State</label>
          <MultiDd
            ariaLabel="State"
            placeholder="All states"
            values={states}
            onChange={setStates}
            options={stateOptions}
            clearable
          />
        </div>
        <div className="payors-f payors-dd">
          <label>Class</label>
          <MultiDd
            ariaLabel="Class"
            placeholder="All classes"
            values={classes}
            onChange={setClasses}
            searchable={false}
            options={PAYOR_CLASSES.map((c) => ({ value: c.key, label: c.label }))}
            clearable
          />
        </div>
        <span className="spacer" />
        {canEdit && (
          <SaveCardAnchor notice={listNote} onClose={() => setListNote(null)}>
            <button className="btn sm" onClick={() => open(blankRcmPayor())}>
              + Add payor
            </button>
          </SaveCardAnchor>
        )}
      </div>
      <p className="payors-count">
        {payors.length} payors · {activeCount} active
      </p>

      {d && (
        <Modal
          title={ro ? d.name || 'Payor details' : d.id ? `Edit ${d.name || 'payor'}` : 'New payor'}
          onClose={requestClose}
          className="payor-modal"
          actions={
            <>
              <button className="btn ghost" onClick={requestClose} disabled={saving}>
                {ro ? 'Close' : 'Cancel'}
              </button>
              {ro ? (
                canEdit && (
                  <button className="btn primary" onClick={() => open(d, false)}>
                    Edit
                  </button>
                )
              ) : (
                <SaveButton
                  label={d.id ? 'Save payor' : 'Add payor'}
                  busy={saving}
                  notice={saveNote}
                  onCloseNotice={() => setSaveNote(null)}
                  onClick={save}
                />
              )}
            </>
          }
        >
          {/* Edits here are the pop-up's own (its Save, its prompt), not Data & settings' unsaved
              changes, so their change events stop here. */}
          <div className="payors-editor" onChange={(e) => e.stopPropagation()}>
            <p className="payors-editor-head muted">
              {ro ? 'Payor details' : "Saved in Revenue Cycle's payor list"}
            </p>
            <div className="py-grid">
              <div className="f py-c-name">
                <label htmlFor="pyName">Payor name *</label>
                <input
                  id="pyName"
                  value={d.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Aetna (CA)"
                  aria-invalid={!!errs.name}
                  disabled={ro}
                  autoFocus={!ro}
                />
                {errs.name && <span className="payors-err">{errs.name}</span>}
              </div>
              <div className="f py-c-class">
                <label>Billing class *</label>
                <Dd
                  value={d.class}
                  onChange={(v) => set('class', v)}
                  searchable={false}
                  ariaLabel="Billing class"
                  placeholder="Pick a class"
                  options={PAYOR_CLASSES.map((c) => ({ value: c.key, label: c.label }))}
                  disabled={ro}
                />
                {errs.class && <span className="payors-err">{errs.class}</span>}
              </div>
              <div className="f py-c-pid">
                <label htmlFor="pyPayerId">Payer ID</label>
                <input
                  id="pyPayerId"
                  value={d.payerId}
                  onChange={(e) => set('payerId', e.target.value)}
                  placeholder="Clearinghouse ID"
                  disabled={ro}
                />
              </div>
              <div className="f py-c-street">
                <label htmlFor="pyStreet">Claims address</label>
                <input
                  id="pyStreet"
                  value={d.street}
                  onChange={(e) => set('street', e.target.value)}
                  placeholder="Street"
                  disabled={ro}
                />
              </div>
              <div className="f py-c-city">
                <label htmlFor="pyCity">City</label>
                <input
                  id="pyCity"
                  value={d.city}
                  onChange={(e) => set('city', e.target.value)}
                  disabled={ro}
                />
              </div>
              <div className="f py-c-state">
                <label>State</label>
                <Dd
                  value={d.addrState}
                  onChange={(v) => set('addrState', v)}
                  ariaLabel="State"
                  placeholder="—"
                  options={[
                    { value: '', label: '—' },
                    ...PAYOR_STATES.map((st) => ({ value: st, label: st })),
                  ]}
                  disabled={ro}
                  floating
                />
              </div>
              <div className="f py-c-zip">
                <label htmlFor="pyZip">ZIP</label>
                <input
                  id="pyZip"
                  value={d.zip}
                  onChange={(e) => set('zip', e.target.value)}
                  aria-invalid={!!errs.zip}
                  inputMode="numeric"
                  maxLength={10}
                  disabled={ro}
                />
                {errs.zip && <span className="payors-err">{errs.zip}</span>}
              </div>
              <div className="f py-c-date">
                <label>Contract start</label>
                <DateField
                  value={d.contractStart}
                  onChange={(v) => set('contractStart', v)}
                  disabled={ro}
                />
              </div>
              <div className="f py-c-date">
                <label>Contract end</label>
                <DateField
                  value={d.contractEnd}
                  onChange={(v) => set('contractEnd', v)}
                  min={d.contractStart || undefined}
                  disabled={ro}
                />
                {errs.contractEnd && <span className="payors-err">{errs.contractEnd}</span>}
              </div>
              <div className="f payors-active py-c-active">
                <label htmlFor="pyActive">Active</label>
                <label className="sw">
                  <input
                    id="pyActive"
                    type="checkbox"
                    checked={d.active}
                    onChange={(e) => set('active', e.target.checked)}
                    disabled={ro}
                  />
                  <span />
                </label>
                <span className="help">
                  Off payors leave the pickers; clients and claims keep them.
                </span>
              </div>
            </div>
            {saveErr && <div className="note warn">{saveErr}</div>}
            {d.id ? (
              <PayorUsers payorId={d.id} payorName={d.name} canEdit={usersEditable(canEdit, ro)} />
            ) : (
              <p className="py-users-empty py-users-new">
                Save the payor first, then add the users who accept it.
              </p>
            )}
          </div>
        </Modal>
      )}
      {ask && d && (
        <ConfirmDialog
          message={
            <>
              <b>Save your changes?</b>
              <p className="pn-modal-p">
                You changed {d.name.trim() || 'this payor'} and haven&rsquo;t saved it.
              </p>
            </>
          }
          cancelLabel="Go back"
          altLabel="Don't save"
          onAlt={close}
          confirmLabel="Save"
          danger={false}
          focusConfirm
          busy={saving}
          onConfirm={save}
          onClose={() => setAsk(false)}
        />
      )}

      <div className="tbl-wrap">
        <table className="tbl payors-tbl">
          <thead>
            <tr>
              <th className="py-name">Payor</th>
              <th>Billing class</th>
              <th>Payer ID</th>
              <th>State</th>
              <th>Contract end</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {groups.length ? (
              groups.map(([st, rows]) => [
                <tr key={`g-${st}`} className="payors-grp">
                  <td colSpan={6}>
                    {st} <span>· {rows.length}</span>
                  </td>
                </tr>,
                ...rows.map((p) => {
                  const cs = contractState(p.contractEnd, today);
                  return (
                    <tr key={p.id} className={p.active ? undefined : 'payors-off'}>
                      <td>{p.name}</td>
                      <td className="muted">{payorClassLabel(p.class) || '—'}</td>
                      <td className="muted">{p.payerId || '—'}</td>
                      <td className="muted">{p.addrState || '—'}</td>
                      <td
                        className={cs ? `payors-date-${cs}` : 'muted'}
                        title={
                          cs === 'expired'
                            ? 'Contract end date — expired'
                            : cs === 'expiring'
                              ? 'Contract end date — expiring soon'
                              : 'Contract end date'
                        }
                      >
                        {p.contractEnd ? fmtDate(p.contractEnd) : '—'}
                      </td>
                      <td>
                        <div className="payors-acts">
                          <button className="btn sm" onClick={() => open(toDraft(p), true)}>
                            View
                          </button>
                          {canEdit && (
                            <button className="btn sm" onClick={() => open(toDraft(p))}>
                              Edit
                            </button>
                          )}
                          <label
                            className="sw"
                            title={
                              canEdit
                                ? p.active
                                  ? 'Active. Click to turn off.'
                                  : 'Off. Click to turn on.'
                                : p.active
                                  ? 'Active'
                                  : 'Off'
                            }
                          >
                            <input
                              type="checkbox"
                              checked={p.active}
                              disabled={!canEdit || toggling === p.id}
                              onChange={() => toggle(p)}
                              aria-label={`${p.name} active`}
                            />
                            <span />
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                }),
              ])
            ) : (
              <tr>
                <td colSpan={6} className="empty">
                  {payors.length
                    ? 'No payors match. Adjust the filters or add one.'
                    : 'No payors yet. Add one, or load them in Revenue Cycle.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="foot-note">
        Rates, fee schedules, import / export and removing a payor are in{' '}
        <a href={RCM_BASE} target="_blank" rel="noreferrer">
          Revenue Cycle
        </a>
        .
      </p>
    </div>
  );
}
